import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { LimitExceededDetails, APIErrorResponse } from '../types/billing';

/**
 * Custom error class for plan limit exceeded errors
 */
export class LimitExceededError extends Error {
  public readonly details: LimitExceededDetails;
  public readonly statusCode = 402;

  constructor(message: string, details: LimitExceededDetails) {
    super(message);
    this.name = 'LimitExceededError';
    this.details = details;
  }
}

/**
 * Check if an error is a LimitExceededError
 */
export function isLimitExceededError(error: unknown): error is LimitExceededError {
  return error instanceof LimitExceededError;
}

/**
 * Extract limit exceeded details from an axios error response
 */
export function extractLimitDetails(error: AxiosError<APIErrorResponse>): LimitExceededDetails | null {
  if (error.response?.status === 402 && error.response.data?.limit_details) {
    return error.response.data.limit_details;
  }
  return null;
}

// Global handler for limit exceeded errors - set by LimitExceededProvider
let limitExceededHandler: ((details: LimitExceededDetails) => void) | null = null;

/**
 * Set the global handler for limit exceeded errors.
 * This is called by LimitExceededProvider to show upgrade modals.
 */
export function setLimitExceededHandler(handler: (details: LimitExceededDetails) => void) {
  limitExceededHandler = handler;
}

/**
 * Clear the limit exceeded handler
 */
export function clearLimitExceededHandler() {
  limitExceededHandler = null;
}

/**
 * Base API client for KeyEnv
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter function - will be set by AuthProvider
let tokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the function used to get the auth token.
 * This should be called by the auth provider when the app initializes.
 */
export function setTokenGetter(getter: () => Promise<string | null>) {
  tokenGetter = getter;
}

/**
 * Clear the token getter (e.g., on sign out)
 */
export function clearTokenGetter() {
  tokenGetter = null;
}

/**
 * Check if a token getter is configured (user is authenticated)
 */
export function hasTokenGetter(): boolean {
  return tokenGetter !== null;
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip if Authorization header is already set
    if (config.headers.Authorization) {
      return config;
    }

    // Get token if getter is available
    if (tokenGetter) {
      try {
        const token = await tokenGetter();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<APIErrorResponse>) => {
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        // Token expired or invalid - let the app handle this
        console.error('API: Unauthorized - token may be expired');
      }

      if (status === 402) {
        // Plan limit exceeded
        const limitDetails = extractLimitDetails(error);
        if (limitDetails) {
          console.warn('API: Plan limit exceeded', limitDetails);
          // Trigger global handler if set
          if (limitExceededHandler) {
            limitExceededHandler(limitDetails);
          }
          // Throw a typed error for easier handling
          return Promise.reject(
            new LimitExceededError(data?.error ?? 'Plan limit exceeded', limitDetails)
          );
        }
      }

      if (status === 403) {
        console.error('API: Forbidden - insufficient permissions');
      }

      if (status >= 500) {
        console.error('API: Server error', error.response.data);
      }
    } else if (error.request) {
      console.error('API: No response received', error.request);
    } else {
      console.error('API: Request setup error', error.message);
    }

    return Promise.reject(error);
  }
);
